import { Autocomplete, Box, Checkbox, Chip, FormControlLabel, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import CustomAxios from '../../utils/CustomAxios';
import { STOCK_LABELS } from '../../utils/moments';

const TONE = {
    in_stock: { bg: '#E3F4EE', fg: '#0B7A5E' },
    out_of_stock: { bg: '#FDECEA', fg: '#B42318' },
    not_sold: { bg: '#F1ECF4', fg: '#6E6478' },
};

/**
 * Pick the product a moment's button opens, by name.
 *
 * Only products sold at the audience's hubs are listed (a switch shows the
 * rest), each with its stock at every one of those hubs. The warning below
 * comes from the preview: it counts real customers per hub, so a hub with
 * nobody in the audience never raises it.
 */
export function ProductPicker({ value, onChange, hubIds, product, error }) {
    const [input, setInput] = useState('');
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [other, setOther] = useState(false);
    const seq = useRef(0);
    const hubsParam = (hubIds || []).join(',');

    // A saved moment only has the id: look it up once so the name shows.
    useEffect(() => {
        if (!value || (selected && String(selected.id) === String(value))) return;
        CustomAxios.get('moments/products/', { params: { search: String(value), hub_ids: hubsParam } })
            .then((res) => {
                const found = (res.data?.results || []).find((p) => String(p.id) === String(value));
                if (found) setSelected(found);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        const mine = ++seq.current;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { search: input, hub_ids: hubsParam, ...(other ? { all: 1 } : {}) };
                const res = await CustomAxios.get('moments/products/', { params });
                if (mine === seq.current) setOptions(res.data?.results || []);
            } catch (e) {
                if (mine === seq.current) setOptions([]);
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [input, hubsParam, other]);

    const short = (product?.hubs || []).filter((h) => h.status !== 'in_stock');
    const shortCustomers = short.reduce((n, h) => n + h.customers, 0);

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
                size="small"
                options={options}
                loading={loading}
                value={selected}
                filterOptions={(x) => x}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(p) => p?.name || ''}
                onChange={(_, p) => { setSelected(p); onChange(p ? String(p.id) : ''); }}
                onInputChange={(_, v, reason) => { if (reason === 'input') setInput(v); }}
                noOptionsText={hubsParam && !other ? "None of your audience's hubs sells a product by that name." : 'No product found.'}
                renderOption={(props, p) => (
                    <li {...props} key={p.id}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14 }} noWrap>{p.name}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5, mt: .25 }}>
                                {(p.stock || []).length
                                    ? p.stock.map((s) => (
                                        <Chip key={s.hub_id} size="small" label={`${s.name} · ${STOCK_LABELS[s.status]}`}
                                            sx={{ height: 20, fontSize: 11, bgcolor: TONE[s.status].bg, color: TONE[s.status].fg }} />
                                    ))
                                    : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>not stocked at any hub</Typography>}
                            </Box>
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label="Product" placeholder="Search by name" error={!!error} helperText={error} />
                )}
            />
            <FormControlLabel
                sx={{ mt: .25, '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' } }}
                control={<Checkbox size="small" checked={other} onChange={(e) => setOther(e.target.checked)} />}
                label="Also show products your audience's hubs don't sell"
            />
            {!error && short.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#8A5A00', bgcolor: '#FFF4DC', borderRadius: 2, px: 1, py: .75 }}>
                    {short.map((h) => `${STOCK_LABELS[h.status] === 'not sold' ? 'Not sold' : 'Out of stock'} at ${h.name}`).join(' · ')}.{' '}
                    {shortCustomers.toLocaleString('en-IN')} customer{shortCustomers === 1 ? '' : 's'} there will see "Shop now" and land on the home page instead.
                </Typography>
            )}
        </Box>
    );
}

export default ProductPicker;
